import React, { useState, useEffect, useMemo } from 'react';
import { Editor } from 'primereact/editor';
import ProtectedRoute from '../../routes/protected';
import { useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import surveyService from '../../services/data/survey.service';
import SurveyPageHeader from './SurveyPageHeader';
import SurveyTemplateAddQuestionDialog from './SurveyTemplateAddQuestionDialog';
import { isRichTextEmpty } from '../../helpers/richText';
import { renderHeaderEditor } from '../../helpers/editorToolbar';
import { onSurveyDescriptionEditorLoad } from '../../helpers/surveyDescriptionEditor';

const DEFAULT_LIKERT_OPTIONS = {
    '1': 'Discordo totalmente',
    '2': 'Discordo',
    '3': 'Neutro / Às vezes',
    '4': 'Concordo',
    '5': 'Concordo totalmente',
};

const emptyDraft = () => ({
    text: '',
    question_type: 'NPS',
    anchor_low: '',
    anchor_high: '',
    likert_options: { ...DEFAULT_LIKERT_OPTIONS },
    comment_optional: false,
    comment_required_if_low: false,
});

export default function SurveyTemplateDetail() {
    const { id } = useParams();
    const location = useLocation();
    const toast = React.useRef(null);
    const VerifyPerms = UsePerms();
    const { rootUser } = useCompanyData();
    const companyId = localStorage.getItem('companyId');
    const canEditQuestion =
        VerifyPerms(['change_surveyquestion']) || VerifyPerms(['change_surveytemplate']);
    const canEditTemplateDescription = VerifyPerms(['change_surveytemplate']) || rootUser;

    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState(emptyDraft);
    const [savingId, setSavingId] = useState(null);
    const [showAddQuestion, setShowAddQuestion] = useState(false);
    const [editingTemplateInfo, setEditingTemplateInfo] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const [savingTemplateInfo, setSavingTemplateInfo] = useState(false);
    const headerEditor = useMemo(() => renderHeaderEditor(), []);

    function loadTemplate() {
        if (!id) return;
        setLoading(true);
        surveyService.getTemplateDetail({ id })
            .then(r => setTemplate(r))
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: err.response?.data?.detail || 'Falha ao carregar' });
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        loadTemplate();
    }, [id]);

    useEffect(() => {
        if (!loading && template && location.state?.openAddQuestion) {
            setShowAddQuestion(true);
        }
    }, [loading, template, location.state?.openAddQuestion]);

    const sortedQuestions = useMemo(() => {
        if (!template?.questions?.length) return [];
        return [...template.questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [template]);

    function startEdit(q) {
        setEditingId(q.id);
        setDraft({
            text: q.text || '',
            question_type: q.question_type || 'NPS',
            anchor_low: q.anchor_low || '',
            anchor_high: q.anchor_high || '',
            likert_options: q.resolved_likert_options || { ...DEFAULT_LIKERT_OPTIONS },
            comment_optional: !!q.comment_optional,
            comment_required_if_low: !!q.comment_required_if_low,
        });
    }

    function cancelEdit() {
        setEditingId(null);
        setDraft(emptyDraft());
    }

    function handleSaveQuestion(questionId) {
        if (!companyId) return;
        setSavingId(questionId);
        const payload = {
            company_id: companyId,
            question_id: questionId,
            text: draft.text,
            question_type: draft.question_type,
            comment_optional: draft.comment_optional,
            comment_required_if_low: draft.comment_required_if_low,
        };
        if (draft.question_type === 'NPS') {
            payload.anchor_low = draft.anchor_low;
            payload.anchor_high = draft.anchor_high;
        } else {
            payload.likert_options = draft.likert_options;
        }
        surveyService.updateTemplateQuestion(payload)
            .then((updated) => {
                setTemplate(prev => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        questions: prev.questions.map((q) => (q.id === updated.id ? { ...q, ...updated } : q)),
                    };
                });
                if (toast.current) toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Pergunta atualizada' });
                cancelEdit();
            })
            .catch((err) => {
                const d = err.response?.data?.detail;
                const msg = Array.isArray(d) ? d.join(' ') : (d || 'Falha ao salvar');
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: msg });
            })
            .finally(() => setSavingId(null));
    }

    function notifyToast(opts) {
        if (toast.current) toast.current.show(opts);
    }

    function startEditTemplateInfo() {
        setTitleDraft(template?.name || '');
        setDescriptionDraft(template?.description || '');
        setEditingTemplateInfo(true);
    }

    function cancelEditTemplateInfo() {
        setEditingTemplateInfo(false);
        setTitleDraft('');
        setDescriptionDraft('');
    }

    function handleSaveTemplateInfo() {
        if (!companyId || !id || !template) return;
        const description = isRichTextEmpty(descriptionDraft) ? '' : descriptionDraft;
        const payload = {
            company_id: companyId,
            template_id: id,
            description,
        };
        if (!template.is_default) {
            const name = titleDraft.trim();
            if (!name) {
                notifyToast({ severity: 'warn', summary: 'Atenção', detail: 'Informe o nome do template.' });
                return;
            }
            payload.name = name;
        }
        setSavingTemplateInfo(true);
        surveyService
            .updateTemplate(payload)
            .then((updated) => {
                setTemplate(updated);
                setEditingTemplateInfo(false);
                setTitleDraft('');
                setDescriptionDraft('');
                notifyToast({ severity: 'success', summary: 'Sucesso', detail: 'Template atualizado' });
            })
            .catch((err) => {
                const d = err.response?.data?.detail;
                const msg = Array.isArray(d) ? d.join(' ') : d || 'Falha ao salvar';
                notifyToast({ severity: 'error', summary: 'Erro', detail: msg });
            })
            .finally(() => setSavingTemplateInfo(false));
    }

    const headerTitle =
        (editingTemplateInfo ? titleDraft : template?.name) || 'Detalhe do Template';

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid">
                <SurveyPageHeader title={headerTitle} />
                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>
                    <div className="d-flex align-items-center mb-3">
                        <a href="/survey/template/list/" className="nav-link text-green fw-bold p-0 text-decoration-none">
                            <FontAwesomeIcon icon={solid('arrow-left')} /> Voltar à lista
                        </a>
                    </div>

                    {loading ? (
                        <div className="text-center py-5 text-muted">Carregando...</div>
                    ) : template ? (
                        <div className="bg-white rounded border-clean p-4">
                            {editingTemplateInfo ? (
                                <div className="mb-4 border rounded p-3 bg-light">
                                    <h6 className="mb-3 text-green">
                                        Editar template
                                    </h6>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Nome</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            maxLength={200}
                                            value={titleDraft}
                                            onChange={(e) => setTitleDraft(e.target.value)}
                                            disabled={template.is_default}
                                            readOnly={template.is_default}
                                        />
                                        {template.is_default && (
                                            <div className="form-text">Templates padrão não permitem alterar o nome.</div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Descrição</label>
                                        <Editor
                                            style={{ height: '200px' }}
                                            className="survey-description-editor"
                                            headerTemplate={headerEditor}
                                            value={descriptionDraft}
                                            onLoad={onSurveyDescriptionEditorLoad}
                                            onTextChange={(e) => setDescriptionDraft(e.htmlValue ?? '')}
                                        />
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm btn-rounded px-3"
                                            disabled={savingTemplateInfo || (!template.is_default && !titleDraft.trim())}
                                            onClick={handleSaveTemplateInfo}
                                        >
                                            {savingTemplateInfo ? 'Salvando...' : 'Salvar alterações'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm btn-rounded px-3"
                                            disabled={savingTemplateInfo}
                                            onClick={cancelEditTemplateInfo}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-3">
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                        <h5 className="mb-0">{template.name}</h5>
                                        {canEditTemplateDescription && (
                                            <button
                                                type="button"
                                                className="btn btn-link border btn-rounded text-green"
                                                onClick={startEditTemplateInfo}
                                            >
                                                <FontAwesomeIcon icon={solid('pen')} className="font095" />
                                            </button>
                                        )}
                                    </div>
                                    {!isRichTextEmpty(template.description) ? (
                                        <div
                                            className="text-muted survey-template-description-html"
                                            dangerouslySetInnerHTML={{ __html: template.description }}
                                        />
                                    ) : (
                                        <p className="text-muted small mb-0">Nenhuma descrição cadastrada.</p>
                                    )}
                                </div>
                            )}
                            <p className="small text-muted mb-4">
                                {template.is_default && <span className="badge bg-primary me-1">Padrão</span>}
                                Versão {template.version}
                                <span className="ms-2">
                                    · {sortedQuestions.length} pergunta(s) · escala numérica de <strong>0</strong> a <strong>10</strong>
                                </span>
                            </p>

                            {template.dimensions?.length > 0 && (
                                <div className="mt-4 mb-4">
                                    <h6>Dimensões</h6>
                                    <ul className="list-group">
                                        {template.dimensions.map(d => (
                                            <li key={d.id} className="list-group-item">
                                                <strong>{d.name}</strong>
                                                {d.description && <div className="small text-muted">{d.description}</div>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-4" style={{ minHeight: '500px', paddingBottom: '200px' }}>
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                    <h6 className="mb-0">Perguntas</h6>
                                    {canEditQuestion && (
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm btn-rounded px-3"
                                            onClick={() => setShowAddQuestion(true)}
                                        >
                                            <FontAwesomeIcon icon={solid('plus')} className="me-1" />
                                            Adicionar pergunta
                                        </button>
                                    )}
                                </div>
                                <p className="small text-muted mb-3">
                                    Cada pergunta é respondida em uma escala conforme o tipo: NPS de 0 a 10 ou Likert de 1 a 5. Os textos abaixo definem o significado do <strong>0</strong> (mínimo) e do <strong>10</strong> (máximo) para NPS e do <strong>1</strong> (mínimo) e do <strong>5</strong> (máximo) para Likert.
                                </p>

                                {canEditQuestion && companyId && id && (
                                    <SurveyTemplateAddQuestionDialog
                                        visible={showAddQuestion}
                                        onHide={() => setShowAddQuestion(false)}
                                        companyId={companyId}
                                        templateId={id}
                                        dimensions={template.dimensions || []}
                                        onCreated={(created) => {
                                            setTemplate((prev) => {
                                                if (!prev) return prev;
                                                return { ...prev, questions: [...(prev.questions || []), created] };
                                            });
                                            notifyToast({ severity: 'success', summary: 'Sucesso', detail: 'Pergunta adicionada' });
                                        }}
                                        onNotify={notifyToast}
                                    />
                                )}

                                {sortedQuestions.length === 0 && (
                                    <p className="text-muted small mb-3">Nenhuma pergunta neste template ainda.</p>
                                )}

                                <div className="d-flex flex-column gap-3">
                                    {sortedQuestions.map((q, idx) => {
                                            const isEditing = editingId === q.id;
                                            return (
                                                <div key={q.id} className="border rounded p-3 bg-light">
                                                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                                                        <span className="badge bg-secondary">#{idx + 1}</span>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {q.dimension_name && (
                                                                <span className="badge bg-light border text-default">{q.dimension_name}</span>
                                                            )}
                                                            {q.is_engagement_question && (
                                                                <span className="badge bg-success">Engajamento (eNPS)</span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {isEditing ? (
                                                        <>
                                                            <div className="mb-2">
                                                                <label className="form-label small fw-bold">Texto da pergunta</label>
                                                                <textarea className="form-control form-control-sm" rows={3} value={draft.text} onChange={e => setDraft({ ...draft, text: e.target.value })} />
                                                            </div>
                                                            <div className="mb-2">
                                                                <label className="form-label small fw-bold">Tipo de pergunta</label>
                                                                <div className="d-flex gap-3">
                                                                    <div className="form-check">
                                                                        <input type="radio" className="form-check-input" id={`type-nps-${q.id}`} checked={draft.question_type === 'NPS'} onChange={() => setDraft({ ...draft, question_type: 'NPS' })} />
                                                                        <label className="form-check-label" htmlFor={`type-nps-${q.id}`}>NPS (0–10)</label>
                                                                    </div>
                                                                    <div className="form-check">
                                                                        <input type="radio" className="form-check-input" id={`type-likert-${q.id}`} checked={draft.question_type === 'LIKERT'} onChange={() => setDraft({ ...draft, question_type: 'LIKERT' })} />
                                                                        <label className="form-check-label" htmlFor={`type-likert-${q.id}`}>Likert (1–5)</label>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {draft.question_type === 'NPS' ? (
                                                                <div className="row g-2 mb-2">
                                                                    <div className="col-md-6">
                                                                        <label className="form-label small fw-bold">Rótulo do 0 (mínimo)</label>
                                                                        <input type="text" className="form-control form-control-sm" value={draft.anchor_low} onChange={e => setDraft({ ...draft, anchor_low: e.target.value })} maxLength={100} />
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <label className="form-label small fw-bold">Rótulo do 10 (máximo)</label>
                                                                        <input type="text" className="form-control form-control-sm" value={draft.anchor_high} onChange={e => setDraft({ ...draft, anchor_high: e.target.value })} maxLength={100} />
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="mb-2">
                                                                    <label className="form-label small fw-bold">Opções de resposta (editáveis)</label>
                                                                    <div className="d-flex flex-column gap-1">
                                                                        {['1','2','3','4','5'].map(key => (
                                                                            <div key={key} className="input-group input-group-sm">
                                                                                <span className="input-group-text" style={{ minWidth: '32px' }}>{key}</span>
                                                                                <input type="text" className="form-control form-control-sm" value={draft.likert_options?.[key] || ''} onChange={e => setDraft({ ...draft, likert_options: { ...draft.likert_options, [key]: e.target.value } })} maxLength={100} />
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            <div className="form-check form-check-inline small mb-2">
                                                                <input type="checkbox" className="form-check-input" id={`co-${q.id}`} checked={draft.comment_optional} onChange={e => setDraft({ ...draft, comment_optional: e.target.checked })} />
                                                                <label className="form-check-label" htmlFor={`co-${q.id}`}>Comentário opcional</label>
                                                            </div>
                                                            <div className="form-check form-check-inline small mb-3">
                                                                <input type="checkbox" className="form-check-input" id={`cr-${q.id}`} checked={draft.comment_required_if_low} onChange={e => setDraft({ ...draft, comment_required_if_low: e.target.checked })} />
                                                                <label className="form-check-label" htmlFor={`cr-${q.id}`}>
                                                                    {draft.question_type === 'LIKERT' ? 'Exigir comentário se nota ≤ 2' : 'Exigir comentário se nota ≤ 6'}
                                                                </label>
                                                            </div>
                                                            <div className="d-flex gap-2">
                                                                <button type="button" className="btn btn-success btn-sm btn-rounded px-3" disabled={savingId === q.id || !draft.text.trim()} onClick={() => handleSaveQuestion(q.id)}>
                                                                    {savingId === q.id ? 'Salvando...' : 'Salvar'}
                                                                </button>
                                                                <button type="button" className="btn btn-outline-secondary btn-sm btn-rounded px-3" disabled={savingId === q.id} onClick={cancelEdit}>
                                                                    Cancelar
                                                                </button>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                                <span className="badge bg-light border text-muted small">
                                                                    {q.question_type === 'LIKERT' ? 'Likert 1–5' : 'NPS 0–10'}
                                                                </span>
                                                            </div>
                                                            <p className="mb-2 fw-medium">{q.text}</p>
                                                            <div className="small border-start border-3 border-success ps-2 mb-2 bg-white rounded p-2">
                                                                <div className="text-muted mb-1">Escala apresentada ao colaborador</div>
                                                                {q.question_type === 'LIKERT' ? (
                                                                    <div className="d-flex flex-column gap-1">
                                                                        {['1','2','3','4','5'].map(k => (
                                                                            <span key={k}><strong>{k}</strong> — {q.resolved_likert_options?.[k] || '—'}</span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <div><strong>0</strong> — {q.anchor_low || '—'}</div>
                                                                        <div className="my-1 text-center text-muted">…</div>
                                                                        <div><strong>10</strong> — {q.anchor_high || '—'}</div>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="small text-muted mb-2">
                                                                {q.comment_optional && <span className="me-2">Comentário opcional.</span>}
                                                                {q.comment_required_if_low && (
                                                                    <span>Comentário obrigatório se nota ≤ {q.question_type === 'LIKERT' ? '2' : '6'}.</span>
                                                                )}
                                                                {!q.comment_optional && !q.comment_required_if_low && <span>Sem campo de comentário extra.</span>}
                                                            </div>
                                                            {canEditQuestion && (
                                                                <button type="button" className="btn btn-outline-success btn-sm btn-rounded px-3" onClick={() => startEdit(q)}>
                                                                    <FontAwesomeIcon icon={solid('pen')} className="me-1" /> Editar pergunta
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="alert alert-warning">Template não encontrado.</div>
                    )}
                </div>
            </div>
        </ProtectedRoute>
    );
}
